// src/data/claimTimeline.js
//
// THE MEMBER'S CLAIM TIMELINE (Record 690; Bob 2026-10-14, "Address, fix and finish 3-6", suggestion 6). A member opening
// his claim met the claim check, the form as it prints, and - folded away under "History of changes" - a table of field
// changes (ruling 86). Nothing on the page said, in order and in words: what has happened to the claim, where it is now,
// and what comes next and by when. This builds that, from what the claim ALREADY records - its own dates (createdAt,
// submittedAt, acceptedAt, presentedOn, carrierAnsweredOn, ...), its appeal-ladder acts (claimActs), the history rows the
// member may read (claims/{id}/history), and the clocks every desk reads (deadlineAlerts.clocksOf, which is the union
// calendar's own list). Nothing is inferred: an event with no recorded date is not placed on the line, and a presentment
// with no recorded proof of receipt says so. The platform decides no timeliness question (claimRequirements E3).
// Pure. Drawn by src\components\ClaimTimeline.js on the claim page.
import { LADDER, OFF_LADDER } from './claimLadder';
import { OUTCOMES } from './claimOutcome';
import { claimActs } from './offProperty';
import { ACTS, answerBoxLabel } from './appealLadder';
import { clocksOf, daysLeft, dayName } from './deadlineAlerts';

const s = (v) => (v === undefined || v === null ? '' : String(v).trim());
const STAGE = Object.fromEntries(LADDER.map((x) => [x.key, x.label]));
const OUTCOME = Object.fromEntries(OUTCOMES.map((x) => [x.key, x.label]));

// A day (YYYY-MM-DD) from a Firestore Timestamp (either SDK), a Date, millis or an ISO string; '' when unknown.
export function dayOf(t) {
  if (!t) return '';
  let ms = NaN;
  if (typeof t.toDate === 'function') ms = t.toDate().getTime();
  else if (typeof t.seconds === 'number') ms = t.seconds * 1000;
  else if (typeof t._seconds === 'number') ms = t._seconds * 1000;
  else if (t instanceof Date) ms = t.getTime();
  else if (typeof t === 'number') ms = t;
  else if (/^\d{4}-\d{2}-\d{2}/.test(String(t))) return String(t).slice(0, 10);
  else ms = Date.parse(String(t));
  return Number.isFinite(ms) ? new Date(ms).toISOString().slice(0, 10) : '';
}

// What has happened, oldest first: [{ date, what, who? }].
export function pastEvents(claim, historyRows) {
  const c = claim || {};
  const out = [];
  const add = (key, date, what) => { if (date && !out.some((e) => e.key === key)) out.push({ key, date, what }); };
  add('written', dayOf(c.createdAt), 'Written');
  add('sent', dayOf(c.submittedAt), 'Sent to the union');
  add('accepted', dayOf(c.acceptedAt), 'Accepted for the union' + (s(c.acceptedBy) ? ' by ' + s(c.acceptedBy) : '') + ' (Intake)');
  // Stage moves, dated by the history rows the member may read (ruling 86). Rows are newest first on the page; any order here.
  const rows = (Array.isArray(historyRows) ? historyRows : []).filter((h) => h && h.field === 'status' && s(h.after));
  rows.sort((a, b) => dayOf(a.timestamp).localeCompare(dayOf(b.timestamp)));
  for (const h of rows) {
    const to = s(h.after), d = dayOf(h.timestamp);
    if (to === 'submitted') add('sent', d, 'Sent to the union');
    else if (to === 'intake') add('accepted', d, 'Accepted for the union (Intake)');
    else if (to === 'gc_review') add('status-gc_review-' + d, d, 'Sent up to the General Chairperson (GC Review)');
    else if (to === 'suspended' || to === 'archived') add('status-' + to + '-' + d, d, to === 'suspended' ? 'Suspended' : 'Archived');
    else if (STAGE[to] && to !== 'draft' && to !== 'closure') add('status-' + to + '-' + d, d, 'Moved to ' + STAGE[to]);
  }
  if (s(c.presentedOn)) {
    add('presented', s(c.presentedOn).slice(0, 10), 'Presented to ' + (s(c.presentedTo) || 'the carrier')
      + (s(c.presentmentProof) ? ' - proof of receipt recorded' : ' - no proof of receipt is recorded'));
  }
  for (const a of claimActs(c)) {
    if (!a || a.kind === 'presented' || !ACTS[a.kind]) continue;
    const date = s(a.on).slice(0, 10);
    const outcome = a.kind === 'carrier_answered' || a.kind === 'conference_decided' ? (s(a.outcome) ? ' (' + answerBoxLabel(a.outcome) + ')' : '') : '';
    add('act-' + a.kind + '-' + date, date, ACTS[a.kind].label + outcome);
  }
  if (s(c.carrierAnsweredOn) && !out.some((e) => /^act-carrier_answered-/.test(e.key))) add('answered', s(c.carrierAnsweredOn).slice(0, 10), 'The carrier answered');
  if (s(c.carriageLastAct) === 'withdrawn') add('withdrawn', dayOf(c.carriageSince), 'The member withdrew the submission to the union');
  // Forensic audit 2026-10-16: closedAt and outcomeAt are written nowhere - the close writes outcomeRecordedAt
  // (ClaimDetail handleClose) - so "Closed" was dated by updatedAt, i.e. by whatever last touched the claim. The
  // recording date comes first; the old names and updatedAt remain only for claims closed before it was written.
  if (s(c.outcome)) add('closed', dayOf(c.outcomeRecordedAt) || dayOf(c.closedAt) || dayOf(c.outcomeAt) || dayOf(c.updatedAt), 'Closed: ' + (OUTCOME[s(c.outcome)] || s(c.outcome)) + (s(c.outcomeReason) ? ' - "' + s(c.outcomeReason) + '"' : ''));
  return out.sort((a, b) => a.date.localeCompare(b.date)).map(({ key, ...e }) => e);
}

// Where it is now, in one sentence.
export function whereNow(claim) {
  const c = claim || {};
  const st = s(c.status);
  if (st === 'draft') return 'A draft - it is yours alone until you send it to the union.';
  if (s(c.outcome) || st === 'closure') return 'Closed' + (s(c.outcome) ? ': ' + (OUTCOME[s(c.outcome)] || s(c.outcome)) : '') + '.';
  if (OFF_LADDER.indexOf(st) !== -1) return st === 'suspended' ? 'Suspended - the union has paused work on it.' : 'Archived.';
  if (s(c.carriageLastAct) === 'withdrawn') return 'The member withdrew it from the union. While the filing window is open the union may still present it.';
  const stage = STAGE[st] ? 'At the ' + STAGE[st] + ' stage' : 'Filed';
  return stage + (s(c.presentedOn) ? ', presented to the carrier ' + s(c.presentedOn).slice(0, 10) : ', not yet recorded as presented to the carrier') + '.';
}

// What comes next and by when: the clocks running, soonest first, each saying whose time it is.
export function nextDates(claim, nowMs) {
  const c = claim || {};
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  return clocksOf(c).map((k) => {
    const left = daysLeft(k.date, now);
    const whose = s(c.status) === 'draft' ? 'Your time' : (k.party === 'carrier' ? "The carrier's time" : "The union's time");
    return { date: k.date, day: dayName(k.date), left, whose, what: k.title };
  }).filter((x) => x.left !== null).sort((a, b) => a.date.localeCompare(b.date));
}

export function buildTimeline(claim, historyRows, nowMs) {
  return { now: whereNow(claim), past: pastEvents(claim, historyRows), next: nextDates(claim, nowMs) };
}
